import React, { FC, useMemo, useState } from 'react';
import { Button, Modal } from 'antd';
import { IconBackground, SearchInput } from 'common/components';
import { EColor } from 'common/const';
import { InfoYellow } from 'common/svg';
import { getEmail } from 'common/helpers';
import { DEFAULT_VALUE_0 } from 'common/config';
import { IUser } from 'entities/User/User.models';
import { getUserName } from 'entities/User/User.helper';
import { getSubdivisionSettingsBreadcrumbItems, getSubdivisionTreeKeysBySubdivisionId } from 'entities/Settings/Settings.helper';
import { ISubdivisionItem } from 'entities/Subdivision/Subdivision.models';

interface IComponentProps {
  open: boolean;
  placeholder: string;
  loading: boolean;
  userList: IUser[];
  checkSubdivision?: boolean;
  subdivisionList?: ISubdivisionItem[];
  onClose: () => void;
  onAdd: (record: IUser, onSuccess: () => void) => void;
}

export const UserAddModal: FC<IComponentProps> = (props) => {
  const { open, placeholder, loading, userList, checkSubdivision, subdivisionList = [], onClose, onAdd } = props;

  const [search, setSearch] = useState('');
  const [selectedUser, setSelectedUser] = useState<IUser | null>(null);
  const [openNotificationModal, setOpenNotificationModal] = useState<boolean>(false);

  const userListForView = userList.filter((user) => {
    return getUserName.fullName(user.lastName, user.firstName, user.patronymic).toLowerCase().includes(search.toLowerCase());
  });
  const subdivisionNames = useMemo(() => {
    if (!selectedUser?.subdivisions) {
      return [];
    }

    return selectedUser.subdivisions
      .map((item) => {
        const subdivisionTreeKeys = getSubdivisionTreeKeysBySubdivisionId(subdivisionList, item.id.toString());
        const subdivisionBreadcrumbItems = getSubdivisionSettingsBreadcrumbItems(subdivisionList, subdivisionTreeKeys);
        return subdivisionBreadcrumbItems.map((item: any) => item.title).join('/');
      })
      .map((name) => `«${name}»`);
  }, [selectedUser, subdivisionList]);

  const handleClose = () => {
    if (loading) {
      return;
    }

    setSearch('');
    setSelectedUser(null);
    onClose();
    setOpenNotificationModal(false);
  };

  const onAddClick = () => {
    if (selectedUser) {
      onAdd(selectedUser, handleClose);
    }
  };

  const handleAdd = () => {
    if (checkSubdivision && selectedUser?.subdivisions?.length) {
      onClose();
      setOpenNotificationModal(true);
      return;
    }

    onAddClick();
  };

  const onNotificationClose = () => {
    setSearch('');
    setSelectedUser(null);
    setOpenNotificationModal(false);
  };

  return (
    <>
      <Modal
        rootClassName="redesign modal"
        width={400}
        centered
        open={open}
        onCancel={handleClose}
        destroyOnClose
        closeIcon={false}
        footer={[
          <Button key="cancel" className="button-sm secondary" onClick={handleClose} loading={loading}>
            Отмена
          </Button>,
          <Button key="save" className="button-sm primary" loading={loading} disabled={!selectedUser} onClick={handleAdd}>
            Добавить
          </Button>,
        ]}
      >
        <div className="mb-6">
          <SearchInput
            auto
            placeholder={placeholder}
            minLength={DEFAULT_VALUE_0}
            onSearch={async (value) => setSearch(value || '')}
          />
        </div>

        <div className="flex direction-column overflow-y-scroll scrollbar" style={{ height: 285 }}>
          {userListForView.length ? (
            userListForView.map((user) => {
              return (
                <Button
                  key={user.id}
                  className={`p-3 flex align-center text-start user-add-modal__list-item ${user.id === selectedUser?.id ? 'active' : ''}`}
                  onClick={() => setSelectedUser(user)}
                >
                  <IconBackground
                    content={
                      <span className="ff-bold fs-xs color-dark-grey">{getUserName.initials(user.lastName, user.firstName)}</span>
                    }
                    color={EColor.LightGrey}
                  />

                  <div>
                    <div className="mb-2 ff-medium fs-s color-black">
                      {getUserName.fullName(user.lastName, user.firstName, user.patronymic)}
                    </div>

                    <span className="ff-regular fs-xs color-dark-grey">{getEmail(user.email)}</span>
                  </div>
                </Button>
              );
            })
          ) : (
            <div className="flex align-center justify-center flex-1 ff-regular fs-m color-dark-grey">
              Список пользователей пуст
            </div>
          )}
        </div>
      </Modal>

      <Modal
        rootClassName="redesign modal"
        width={440}
        centered
        open={openNotificationModal}
        onCancel={onNotificationClose}
        destroyOnClose
        footer={[
          <Button key="cancel" className="button-sm secondary" onClick={onNotificationClose} loading={loading}>
            Отмена
          </Button>,
          <Button key="accept" className="button-sm primary" loading={loading} onClick={onAddClick}>
            Подтвердить
          </Button>,
        ]}
      >
        <div className="mb-7">
          <IconBackground content={<InfoYellow className="icon-info-yellow" />} color={EColor.PaleYellow} />
        </div>

        <div className="mb-4 ff-medium fs-m">Добавление в подразделение</div>
        <div className="mb-4 ff-regular fs-s color-dark-grey">{`Пользователь ${selectedUser?.email} уже добавлен в ${subdivisionNames}.`}</div>
        <div className="mb-6 ff-regular fs-s color-dark-grey">Вы уверены, что хотите добавить его в ещё одно подразделение?</div>
      </Modal>
    </>
  );
};
